"use client";

import { useMemo } from "react";
import ProviderAvatar from "@/components/provider-avatar";
import { PresenceIndicator } from "@/components/team-chat/presence-indicator";
import { Skeleton } from "@/components/ui/skeleton";
import Tooltip from "@/components/ui/tooltip";
import { useUser } from "@/contexts/user-provider";
import { useProviders } from "@/hooks/use-providers";
import { useTeamChatRealtime } from "@/hooks/use-team-chat-realtime";
import { formatTime } from "@/lib/format-data";
import type { ProviderPresenceInfo } from "@/types/team-chat";
import { useOnlineProviders, useTeamConversations, useTeamUnreadCounts } from "@/query/team-chat";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
const MAX_VISIBLE_AVATARS = 3;
interface AvatarEntry {
  providerId: string;
  status: "ONLINE" | "WITH_CLIENT" | "BUSY" | "OFFLINE";
  tooltip: string;
}
export function WhisperHeaderButton() {
  const {
    currentUser
  } = useUser();
  const {
    providers,
    isLoading: isProvidersLoading,
    providerIdProviderMap
  } = useProviders();
  const {
    data: onlineData,
    isLoading: isOnlineLoading
  } = useOnlineProviders();
  const {
    data: unreadCounts
  } = useTeamUnreadCounts();
  const isLoading = isProvidersLoading || isOnlineLoading;
  const {
    isOpen,
    openDrawer,
    closeDrawer
  } = useTeamChatDrawerStore();

  // Subscribe to real-time updates (always active regardless of panel state)
  useTeamChatRealtime();
  const totalUnread = useMemo(() => Object.values(unreadCounts ?? {}).reduce((sum, c) => sum + c, 0), [unreadCounts]);
  const {
    conversations
  } = useTeamConversations();

  // Build list ordered by most recent conversations, filtering out Decoda staff
  const avatarEntries = useMemo(() => {
    const onlineProviders = (onlineData?.providers ?? []).filter(p => p.providerId !== currentUser?.id);
    const onlineMap = new Map(onlineProviders.map(p_0 => [p_0.providerId, p_0]));

    // Providers from most recent DM conversations (sorted by lastMessageAt)
    const recentDms = [...conversations].filter(c_0 => c_0.type === "DM" && c_0.lastMessageAt).sort((a, b) => new Date(b.lastMessageAt!).getTime() - new Date(a.lastMessageAt!).getTime());
    const seen = new Set<string>();
    const entries: AvatarEntry[] = [];
    for (const conv of recentDms) {
      if (entries.length >= MAX_VISIBLE_AVATARS) break;
      const other = conv.members.find(m => m.providerId !== currentUser?.id);
      if (!other || seen.has(other.providerId)) continue;
      seen.add(other.providerId);
      const presence = onlineMap.get(other.providerId);
      const name = `${other.firstName} ${other.lastName}`.trim() || "Unknown";
      let tooltip = name;
      if (presence?.status === "WITH_CLIENT" && presence.currentAppointment) {
        const endTime = formatTime(presence.currentAppointment.endTime);
        tooltip = endTime ? `${name} · With patient until ${endTime}` : `${name} · With patient`;
      } else if (presence?.status === "BUSY" && presence.currentBlock) {
        const endTime_0 = formatTime(presence.currentBlock.endTime);
        tooltip = endTime_0 ? `${name} · Busy until ${endTime_0}` : `${name} · Busy`;
      } else if (!presence) {
        tooltip = `${name} · Offline`;
      }
      entries.push({
        providerId: other.providerId,
        status: presence?.status ?? "OFFLINE",
        tooltip
      });
    }

    // Fill remaining slots with online providers, then offline
    if (entries.length < MAX_VISIBLE_AVATARS) {
      // Online first
      for (const presence_0 of onlineProviders) {
        if (entries.length >= MAX_VISIBLE_AVATARS) break;
        if (seen.has(presence_0.providerId)) continue;
        const provider = providerIdProviderMap.get(presence_0.providerId);
        seen.add(presence_0.providerId);
        const name_0 = provider ? `${provider.firstName ?? ""} ${provider.lastName ?? ""}`.trim() : "Unknown";
        entries.push({
          providerId: presence_0.providerId,
          status: presence_0.status,
          tooltip: name_0
        });
      }
    }
    if (entries.length < MAX_VISIBLE_AVATARS) {
      // Then offline providers
      for (const p_1 of providers) {
        if (entries.length >= MAX_VISIBLE_AVATARS) break;
        if (p_1.id === currentUser?.id || seen.has(p_1.id)) continue;
        seen.add(p_1.id);
        const name_1 = `${p_1.firstName ?? ""} ${p_1.lastName ?? ""}`.trim() || "Unknown";
        entries.push({
          providerId: p_1.id,
          status: "OFFLINE",
          tooltip: `${name_1} · Offline`
        });
      }
    }
    return entries;
  }, [onlineData, currentUser?.id, conversations, providerIdProviderMap, providers]);
  const presenceMap = useMemo(() => {
    const map = new Map<string, ProviderPresenceInfo>();
    for (const p_2 of onlineData?.providers ?? []) {
      map.set(p_2.providerId, p_2);
    }
    return map;
  }, [onlineData]);
  const visibleEntries = avatarEntries.slice(0, MAX_VISIBLE_AVATARS);
  if (!isLoading && visibleEntries.length === 0 && totalUnread === 0) {
    return null;
  }
  return <button onClick={isOpen ? closeDrawer : openDrawer} data-whisper-trigger className="relative mr-2 hidden items-center gap-1 rounded-lg px-2 py-1 transition-colors hover:bg-hover md:flex">
      {isLoading ? <div className="flex items-center -space-x-1.5">
          {Array.from({
        length: MAX_VISIBLE_AVATARS
      }).map((_, i) => <Skeleton key={i} className="h-6 w-6 rounded-full ring-2 ring-background" />)}
        </div> : visibleEntries.length > 0 ? <div className="flex items-center -space-x-1.5">
          {visibleEntries.map((entry, idx) => <Tooltip key={entry.providerId} content={entry.tooltip} side="bottom" triggerAsChild>
              <div className="relative h-6 w-6" style={{
          zIndex: visibleEntries.length - idx
        }}>
                <PresenceIndicator status={entry.status} size={24}>
                  <ProviderAvatar providerId={entry.providerId} size={24} className="ring-2 ring-background" />
                </PresenceIndicator>
                {presenceMap.get(entry.providerId)?.customStatusEmoji && <Tooltip content={presenceMap.get(entry.providerId)?.customStatusText} side="bottom" triggerAsChild>
                    <span className="absolute -top-1 -right-1 text-tiny leading-none">
                      {presenceMap.get(entry.providerId)?.customStatusEmoji}
                    </span>
                  </Tooltip>}
              </div>
            </Tooltip>)}
        </div> : null}

      {/* Unread badge */}
      {totalUnread > 0 && <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-tiny font-medium text-destructive-foreground">
          {totalUnread > 99 ? "99+" : totalUnread}
        </span>}
    </button>;
}